/**
 * cvCentre.ts — the decisions the CCTV centre makes, as pure functions.
 *
 * These are separated from the component because they are where the
 * HONESTY lives, and honesty is testable:
 *
 *   - a camera the probe has stopped reporting must say so, not show an
 *     old picture as if it were current;
 *   - a camera that is watched but quiet must say "nothing moving", not
 *     "no data";
 *   - while a feed is still in flight the centre must say "loading", not
 *     "nothing to report" — those are different facts and conflating them
 *     is how a dashboard ends up claiming a street is quiet when the
 *     network simply has not answered yet.
 */

import type { CvCenter, CvDetection } from "@sikhio/shared";

export const CLASS_LABEL: Record<string, string> = {
  motion: "เคลื่อนไหว",
  presence: "อยู่ในเฟรม",
};

export function classLabel(cls: string): string {
  return CLASS_LABEL[cls] ?? cls;
}

/** Age in a human form — "12 นาที" / "3 ชม." / "—". */
export function ageLabel(iso: string | null, now: number): string {
  if (!iso) return "—";
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return "เมื่อสักครู่";
  if (mins < 60) return `${mins} นาที`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} ชม.`;
  return `${Math.floor(hours / 24)} วัน`;
}

/** The strongest detection of an event — the one the operator reads first. */
export function topDetection(detections: CvDetection[]): CvDetection | null {
  if (detections.length === 0) return null;
  return [...detections].sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))[0];
}

/** How long a camera may go without reporting before we call it silent. */
export const STALE_AFTER_MS = 10 * 60 * 1000;

export type CentreState = "stale" | "detected" | "quiet";

export interface CentreVerdict {
  state: CentreState;
  /** The confidence of the strongest detection, or null when there is none. */
  confidence: number | null;
  /** The class of the strongest detection, or null. */
  class: string | null;
  /** When the camera was last seen — by detection, or by heartbeat. */
  lastSeen: string | null;
  age: string;
}

/**
 * What the centre says about one camera.
 *
 * `stale` wins over everything: a camera the probe stopped reporting has
 * no current state, and presenting its last known detection as if it were
 * happening now is the worst kind of wrong.
 */
export function cameraVerdict(cell: CvCenter, now: number): CentreVerdict {
  const aliveMs = cell.lastAlive ? Date.parse(cell.lastAlive) : NaN;
  const aliveFresh = Number.isFinite(aliveMs) && now - aliveMs <= STALE_AFTER_MS;
  const top = topDetection(cell.detections);

  if (!aliveFresh) {
    return {
      state: "stale",
      confidence: null,
      class: null,
      lastSeen: cell.lastAlive ?? cell.lastSeen,
      age: ageLabel(cell.lastAlive ?? cell.lastSeen, now),
    };
  }
  if (top) {
    return {
      state: "detected",
      confidence: top.confidence ?? 0,
      class: top.class,
      lastSeen: cell.lastSeen,
      age: ageLabel(cell.lastSeen, now),
    };
  }
  return {
    state: "quiet",
    confidence: null,
    class: null,
    lastSeen: cell.lastAlive,
    age: ageLabel(cell.lastAlive, now),
  };
}

/**
 * The counts in the centre's header.
 *
 * `events` is null while the event log is still loading, so the header can
 * say "loading" instead of "0 events" — a zero and an unanswered question
 * look identical on a screen.
 */
export function centreSummary(
  cells: CvCenter[],
  eventCount: number | null,
): { cameras: number; withDetection: number; events: string } {
  return {
    cameras: cells.length,
    withDetection: cells.filter((c) => c.detections.length > 0).length,
    events: eventCount == null ? "กำลังโหลดเหตุการณ์…" : `${eventCount} เหตุการณ์`,
  };
}

/**
 * A detection box that is guaranteed to sit inside its frame.
 *
 * WHY THIS EXISTS
 * ---------------
 * The detector rounds the box's origin and its size to three decimals
 * SEPARATELY. A region that ends exactly on the frame's right edge is
 * x = 0.8125, w = 0.1875 — exactly 1.0. Rounded independently that becomes
 * x = 0.813 and w = 0.188, so x + w = 1.001 and the box pokes a tenth of a
 * percent outside the picture.
 *
 * Measured live: the CCTV centre gate failed with "1 detection box(es) fall
 * outside their frame" at 390×844, on a box of x=0.813 w=0.188. The box
 * was drawn off the right edge of the frame the operator is supposed to
 * read. The probe has been fixed to round the EDGES and derive the size, so
 * its boxes are contained by construction — but 280 events already stored
 * in KV carry the old geometry, and a detector is not the only thing that
 * can post to this API.
 *
 * So the renderer clamps as well. The invariant that matters is simple: a
 * box is never drawn outside the picture it belongs to. A box that would be
 * is pulled back to the edge and narrowed to fit — it keeps its meaning
 * (there is activity at that edge) and loses only the sliver that was
 * never inside the frame anyway.
 */
export interface Bbox {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function clampBbox(bbox: Bbox): Bbox {
  const x0 = Math.min(Math.max(bbox.x, 0), 1);
  const y0 = Math.min(Math.max(bbox.y, 0), 1);
  const x1 = Math.min(Math.max(x0 + bbox.w, x0), 1);
  const y1 = Math.min(Math.max(y0 + bbox.h, y0), 1);

  // A box that was already inside is handed back exactly as it came.
  // Reconstructing the size by subtraction would perturb a perfectly good
  // box with float noise (0.5 + 0.1 - 0.5 = 0.09999999999999998) for no
  // reason — the clamp exists to fix broken boxes, not to touch good ones.
  if (bbox.x === x0 && bbox.y === y0 && x1 === x0 + bbox.w && y1 === y0 + bbox.h) {
    return { x: bbox.x, y: bbox.y, w: bbox.w, h: bbox.h };
  }

  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
